import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
    Users, 
    Package, 
    ShoppingCart, 
    Clock,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    ArrowRight,
    ClipboardList,
    RefreshCw,
    Building2,
    FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hubspotSyncing, setHubspotSyncing] = useState({ clients: false, orders: false });
    const [hubspotResult, setHubspotResult] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (error) {
            console.error('Fetch stats error:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // HubSpot sync functions
    const syncHubspotClients = async () => {
        setHubspotSyncing(prev => ({ ...prev, clients: true }));
        setHubspotResult(null);
        try {
            const response = await axios.post(`${API_URL}/admin/hubspot/sync-all-clients`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHubspotResult({ 
                type: 'clients', 
                success: true, 
                message: response.data.message,
                count: response.data.synced?.length || 0
            });
        } catch (error) {
            setHubspotResult({ 
                type: 'clients', 
                success: false, 
                message: error.response?.data?.detail || 'Erreur de synchronisation'
            });
        } finally {
            setHubspotSyncing(prev => ({ ...prev, clients: false }));
        }
    };

    const syncHubspotOrders = async () => {
        setHubspotSyncing(prev => ({ ...prev, orders: true }));
        setHubspotResult(null);
        try {
            const response = await axios.post(`${API_URL}/admin/hubspot/sync-all-orders`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHubspotResult({ 
                type: 'orders', 
                success: true, 
                message: response.data.message,
                count: response.data.synced?.length || 0
            });
        } catch (error) {
            setHubspotResult({ 
                type: 'orders', 
                success: false, 
                message: error.response?.data?.detail || 'Erreur de synchronisation'
            });
        } finally {
            setHubspotSyncing(prev => ({ ...prev, orders: false }));
        }
    };

    const statCards = stats ? [
        {
            title: 'Utilisateurs',
            value: stats.users.total,
            subValue: `${stats.users.pending} en attente`,
            icon: Users,
            color: 'blue',
            link: '/admin/users',
            alert: stats.users.pending > 0
        },
        {
            title: 'Stock disponible',
            value: stats.products.available,
            subValue: `${stats.products.reserved} réservé(s)`,
            icon: Package,
            color: 'emerald',
            link: '/admin/products'
        },
        {
            title: 'Commandes',
            value: stats.orders.total,
            subValue: `${stats.orders.pending} en attente`,
            icon: ShoppingCart,
            color: 'amber',
            link: '/admin/orders',
            alert: stats.orders.pending > 0
        },
        {
            title: 'À préparer',
            value: stats.orders.to_prepare || 0,
            subValue: 'Commande(s) à expédier',
            icon: ClipboardList,
            color: 'orange',
            link: '/admin/preparation',
            alert: (stats.orders.to_prepare || 0) > 0
        },
        {
            title: 'Chiffre d\'affaires',
            value: `${stats.revenue.toLocaleString('fr-FR')} €`,
            subValue: 'Total TTC',
            icon: TrendingUp,
            color: 'violet',
            link: '/admin/orders'
        }
    ] : [];

    const getColorClasses = (color) => {
        const colors = {
            blue: 'bg-blue-50 text-blue-600',
            emerald: 'bg-emerald-50 text-emerald-600',
            amber: 'bg-amber-50 text-amber-600',
            orange: 'bg-orange-50 text-orange-600',
            violet: 'bg-violet-50 text-violet-600'
        };
        return colors[color] || colors.blue;
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8">
                <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                    Tableau de bord
                </h1>

                {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[...Array(4)].map((_, i) => (
                            <Card key={i} className="border-slate-200">
                                <CardContent className="p-6">
                                    <div className="animate-pulse space-y-4">
                                        <div className="h-10 w-10 bg-slate-200 rounded-lg"></div>
                                        <div className="h-8 bg-slate-200 rounded w-1/2"></div>
                                        <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Stats Grid */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
                            {statCards.map((stat, index) => (
                                <Link key={index} to={stat.link}>
                                    <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer h-full" data-testid={`stat-card-${index}`}>
                                        <CardContent className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getColorClasses(stat.color)}`}>
                                                    <stat.icon className="w-6 h-6" />
                                                </div>
                                                {stat.alert && (
                                                    <span className="flex h-3 w-3">
                                                        <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-amber-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</p>
                                            <p className="text-sm text-slate-500">{stat.subValue}</p>
                                            <p className="text-sm font-medium text-slate-900 mt-3">{stat.title}</p>
                                        </CardContent>
                                    </Card>
                                </Link>
                            ))}
                        </div>

                        {/* Quick Actions */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Pending Users */}
                            {stats.users.pending > 0 && (
                                <Card className="border-amber-200 bg-amber-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-amber-800">
                                            <AlertTriangle className="w-5 h-5" />
                                            Comptes en attente
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-amber-700 mb-4">
                                            {stats.users.pending} compte(s) professionnel(s) à valider
                                        </p>
                                        <Link 
                                            to="/admin/users?status=pending" 
                                            className="inline-flex items-center text-amber-800 font-medium hover:underline"
                                            data-testid="pending-users-link"
                                        >
                                            Voir les demandes
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Pending Orders */}
                            {stats.orders.pending > 0 && (
                                <Card className="border-blue-200 bg-blue-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-blue-800">
                                            <Clock className="w-5 h-5" />
                                            Commandes en attente
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-blue-700 mb-4">
                                            {stats.orders.pending} commande(s) en attente de paiement
                                        </p>
                                        <Link 
                                            to="/admin/orders?status=pending" 
                                            className="inline-flex items-center text-blue-800 font-medium hover:underline"
                                            data-testid="pending-orders-link"
                                        >
                                            Voir les commandes
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Orders to Prepare */}
                            {stats.orders.to_prepare > 0 && (
                                <Card className="border-orange-200 bg-orange-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-orange-800">
                                            <ClipboardList className="w-5 h-5" />
                                            Commandes à préparer
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-orange-700 mb-4">
                                            {stats.orders.to_prepare} commande(s) à expédier
                                        </p>
                                        <Link 
                                            to="/admin/preparation" 
                                            className="inline-flex items-center text-orange-800 font-medium hover:underline"
                                            data-testid="prepare-orders-link"
                                        >
                                            Préparer les commandes
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Active Reservations */}
                            {stats.products.reserved > 0 && (
                                <Card className="border-violet-200 bg-violet-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-violet-800">
                                            <Clock className="w-5 h-5" />
                                            Réservations actives
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-violet-700 mb-4">
                                            {stats.products.reserved} produit(s) actuellement réservé(s)
                                        </p>
                                        <Link 
                                            to="/admin/reservations" 
                                            className="inline-flex items-center text-violet-800 font-medium hover:underline"
                                            data-testid="reservations-link"
                                        >
                                            Gérer les réservations
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </CardContent>
                                </Card>
                            )}

                            {/* All Good */}
                            {stats.users.pending === 0 && stats.orders.pending === 0 && stats.products.reserved === 0 && (
                                <Card className="border-emerald-200 bg-emerald-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-emerald-800">
                                            <CheckCircle className="w-5 h-5" />
                                            Tout est à jour
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-emerald-700">
                                            Aucune action urgente requise. Toutes les demandes ont été traitées.
                                        </p>
                                    </CardContent>
                                </Card>
                            )}

                            {/* HubSpot Sync Section */}
                            <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-orange-800">
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.984 2.21 2.21 0 00-4.42 0c0 .873.51 1.627 1.246 1.984v2.847a5.398 5.398 0 00-2.469 1.135L5.444 3.016a2.276 2.276 0 00.157-.823A2.207 2.207 0 003.394 0a2.207 2.207 0 00-2.207 2.193 2.207 2.207 0 002.207 2.207c.406 0 .785-.114 1.11-.308l8.245 5.974a5.4 5.4 0 00-.498 2.26 5.4 5.4 0 00.498 2.26l-8.245 5.974a2.2 2.2 0 00-1.11-.308 2.207 2.207 0 00-2.207 2.207A2.207 2.207 0 003.394 24a2.207 2.207 0 002.207-2.207c0-.294-.06-.574-.157-.823l8.344-6.05a5.398 5.398 0 002.469 1.135v2.847a2.198 2.198 0 00-1.246 1.984 2.21 2.21 0 004.42 0 2.198 2.198 0 00-1.267-1.984V16.07a5.43 5.43 0 10-5.43-5.43 5.398 5.398 0 00.498 2.26"/>
                                        </svg>
                                        Synchronisation HubSpot
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-orange-700 text-sm">
                                        Synchronisez vos clients et commandes vers HubSpot CRM.
                                    </p>
                                    
                                    <div className="flex flex-wrap gap-3">
                                        <Button
                                            onClick={syncHubspotClients}
                                            disabled={hubspotSyncing.clients}
                                            className="bg-orange-600 hover:bg-orange-700 text-white"
                                            data-testid="sync-hubspot-clients"
                                        >
                                            {hubspotSyncing.clients ? (
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Building2 className="w-4 h-4 mr-2" />
                                            )}
                                            {hubspotSyncing.clients ? 'Synchronisation...' : 'Sync Clients'}
                                        </Button>
                                        
                                        <Button
                                            onClick={syncHubspotOrders}
                                            disabled={hubspotSyncing.orders}
                                            className="bg-amber-600 hover:bg-amber-700 text-white"
                                            data-testid="sync-hubspot-orders"
                                        >
                                            {hubspotSyncing.orders ? (
                                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <FileText className="w-4 h-4 mr-2" />
                                            )}
                                            {hubspotSyncing.orders ? 'Synchronisation...' : 'Sync Commandes'}
                                        </Button>
                                    </div>

                                    {hubspotResult && (
                                        <div className={`p-3 rounded-lg text-sm ${
                                            hubspotResult.success 
                                                ? 'bg-green-100 text-green-800 border border-green-200' 
                                                : 'bg-red-100 text-red-800 border border-red-200'
                                        }`}>
                                            {hubspotResult.success ? (
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span>{hubspotResult.message}</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <AlertTriangle className="w-4 h-4" />
                                                    <span>{hubspotResult.message}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
